import type { ReactNode } from 'react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ActiveReviewerPresence } from '@shared/review-presence-api';
import {
    reviewPresenceTooltipLines,
    reviewPresenceActivityLabel,
    selectReviewPresenceDisplay,
} from '@/lib/reviewPresenceByStore';

type StoreReviewPresenceProps = {
    reviewers: ActiveReviewerPresence[];
};

export function StoreReviewPresence({ reviewers }: StoreReviewPresenceProps) {
    if (reviewers.length === 0) {
        return null;
    }

    const { visible, overflow } = selectReviewPresenceDisplay(reviewers);

    return (
        <div className="flex flex-wrap items-center gap-1 max-w-full">
            <span className="text-[11px] text-muted-foreground whitespace-nowrap">Reviewing:</span>
            {visible.map((reviewer) => (
                <Tooltip key={reviewer.userId}>
                    <TooltipTrigger asChild>
                        <span
                            tabIndex={0}
                            className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-amber-200 bg-amber-50 px-1 text-[10px] font-semibold text-amber-900 leading-none cursor-default"
                            aria-label={`${reviewer.name}: ${reviewPresenceActivityLabel(reviewer)}. Work is not locked.`}
                        >
                            {reviewer.initials}
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                        <div className="space-y-0.5">
                            {reviewPresenceTooltipLines(reviewer).map((line) => (
                                <div key={line}>{line}</div>
                            ))}
                        </div>
                    </TooltipContent>
                </Tooltip>
            ))}
            {overflow > 0 && (
                <span className="text-[10px] font-medium text-muted-foreground tabular-nums">
                    +{overflow}
                </span>
            )}
        </div>
    );
}

export function StoreReviewPresenceTooltipRoot({ children }: { children: ReactNode }) {
    return <TooltipProvider delayDuration={200}>{children}</TooltipProvider>;
}
